// @ts-strict-ignore
import { DestroyRef, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, from, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, first, map, mergeMap, switchMap } from 'rxjs/operators';

import { MotorizationVehicle } from 'src/app/api/owcc/models/motorization-vehicle';
import { AppDomContextService } from 'src/app/app-dom-context.service';
import { STORY_NUMBER_FE_AB_TESTING } from 'src/app/core/services/ab-testing/ab-test-runner.service';
import { AbTestingService } from 'src/app/core/services/ab-testing/ab-testing.service';
import { LazyComponentId } from 'src/app/core/services/lazy-components/lazy-components-registry';
import { LinkService } from 'src/app/core/services/link/link.service';
import { PrintService } from 'src/app/core/services/print/print.service';
import { ScrollingService } from 'src/app/core/services/scrolling/scrolling.service';
import { ModalService } from 'src/app/shared/modal/modal-service/modal.service';
import { fromCarl, selectCurrentMotorization } from 'src/app/store/common/common.selectors';
import {
  financingUpdateIsFcisRateConfirmed,
  financingUpdateIsFcisWidgetOpen
} from 'src/app/store/financing/financing.actions';
import { selectIsCCcIAndHasNoDealerSelected } from 'src/app/store/runtime-variables/runtime-variables.selectors';
import { noop } from 'src/app/utils/functions/noop';
import { filterTruthy } from 'src/app/utils/functions/rx-filters';

import { COMPONENT_NAME } from '../wishlist-save-button/wishlist-save-button.component';

import {
  Nba,
  NbaActionType,
  NbaConditionConfig,
  NbaConfig,
  NbaConfigurations,
  NbaConfigurationsWithAbTesting,
  NbaLinkData,
  NbaModalData,
  NbaPosition,
  Nbas,
  NbaScrollToAnchorData,
  OptionallyPrimaryNbaConfigAttachment,
  PermanentNba,
  PrimaryNba,
  PrimaryNbaConfig,
  SecondaryNba,
  SecondaryNbaConfig
} from './nba.model';
import { NbaConditionResolver } from './resolvers/condition/nba-condition-resolver.interface';
import { NbaConditionResolversCollection } from './resolvers/condition/nba-condition-resolvers-collection';
import { NbaParamResolversCollection } from './resolvers/param/nba-param-resolvers-collection';

interface ActiveNbaStorageEntry {
  nbas: NbaConfig[];
  subject: BehaviorSubject<NbaConfig[]>;
}

class ActiveNbasStorage {
  private readonly map: Record<string, ActiveNbaStorageEntry> = {};

  public updateEntry(key: string, nba: SecondaryNba): void {
    const mapEntry = this.getEntry(key);
    const allIds = new Set(mapEntry.nbas.map(config => config.id));
    if (!allIds.has(nba.id) && nba.isActive) {
      /* some nbas have the same id but different content (save config),
       * so we need to check if the nba is active AND if the RIGHT one is in the map */
      mapEntry.nbas.push(nba);
      mapEntry.subject.next(mapEntry.nbas);
    }

    if (allIds.has(nba.id) && !nba.isActive) {
      mapEntry.nbas = mapEntry.nbas.filter(config => config !== nba);
      mapEntry.subject.next(mapEntry.nbas);
    }
  }

  // TODO: Fix with MBCC-2656
  // rewrite and fix typing and document nba logic; current situation is a unfixable type hell
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public nbasAsObservable$(key: string): Observable<any> {
    return this.getEntry(key).subject.asObservable();
  }

  /**
   * Returns the entry for the given nba id. If entry not yet in map, it gets created.
   */
  private getEntry(nbaId: string): ActiveNbaStorageEntry {
    const mapEntry = this.map[nbaId];
    if (!mapEntry) {
      this.map[nbaId] = { nbas: [], subject: new BehaviorSubject<NbaConfig[]>([]) };
    }
    return mapEntry || this.map[nbaId];
  }
}

const PARAM_PATTERN = /{\s*\w+\s*}/g;
const BRACKET_PATTERN = /{+|}+/g;

@Injectable()
export class NbaService {
  private readonly destroyRef = inject(DestroyRef);

  public constructor(
    private readonly store: Store,
    private readonly abTestingService: AbTestingService,
    private readonly nbaConditionResolversCollection: NbaConditionResolversCollection,
    private readonly nbaParamResolversCollection: NbaParamResolversCollection,
    private readonly modalService: ModalService,
    private readonly printService: PrintService,
    private readonly scrollingService: ScrollingService,
    private readonly linkService: LinkService,
    private readonly router: Router,
    private readonly appDomContextService: AppDomContextService
  ) {}

  public getNbaConfig$(): Observable<NbaConfigurationsWithAbTesting> {
    return this.store.select(fromCarl.selectConfig<NbaConfigurationsWithAbTesting>('nbas'));
  }

  /**
   *
   * @returns true if the wishlist is configured in one of the permanent NBAs, false otherwise. Used to show/hide the wishlist button.
   */
  public isWishlistConfigured$(): Observable<boolean> {
    return this.getNbaConfig$().pipe(map(nbaConfig => nbaConfig?.permanentSaveButton?.active ?? false));
  }

  /**
   * Inits the NBAs for the NBA bar, digests the defined jsons and returns observables with the processed NBAs (filtered by conditions and resolved link parameters)
   */
  public initNbaBarData(nbaPosition?: NbaPosition): Observable<Nbas> {
    return this.getNbaConfig$().pipe(
      filterTruthy(),
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      map(nbaConfigurations => {
        const position = nbaPosition ?? 'defaultPosition';
        const primaryNba$ = this.initPrimaryNba$(nbaConfigurations, position);
        const secondaryNbas$ = this.initSecondaryNbas$(nbaConfigurations, position);
        return { primaryNba$, secondaryNbas$ };
      }),
      catchError(() => of({ primaryNba$: of(null), secondaryNbas$: of([]) }))
    );
  }

  /**
   * Inits the NBAs for the permanent NBA dropdown menu
   */
  public initPermanentNbas(nbaConfigurations: NbaConfigurations): Observable<Nba[]> {
    return this.initPermanentNbas$(nbaConfigurations);
  }

  /**
   * If a button is not 'type' of 'link' (which has a href) it will be rendered as a clickable button.
   *
   * Currently supported NBA click actions are:
   * - modal
   * - scroll to anchor
   * - pdf / print download
   * - (wishlist) save configuration / save to account
   */
  public onNbaClick(nbaConfig: NbaConfig): void {
    if (nbaConfig.type === NbaActionType.Modal) this.handleModalActionType(nbaConfig);
    if (nbaConfig.type === NbaActionType.NavigateToAnchor) this.handleNavigateToAnchorActionType(nbaConfig);
    if (nbaConfig.type === NbaActionType.ScrollToAnchor) this.handleScrollToAnchorActionType(nbaConfig);
    if (nbaConfig.type === NbaActionType.Print) this.handlePrintActionType();
    if (nbaConfig.type === NbaActionType.SaveToAccount) this.handleSaveToAccountActionType();
    if (nbaConfig.type === NbaActionType.OpenFinancialCalculator) this.handleOpenFinancialCalculatorActionType();
    if (nbaConfig.type === NbaActionType.MonthlyRateHide) this.handleMonthlyRateHideActionType();
  }

  private handleModalActionType(nbaConfig: NbaConfig): void {
    let componentId = (nbaConfig.data as NbaModalData)?.component;
    if (componentId === 'PermanentSummaryModal') {
      componentId = LazyComponentId.PermanentSummaryModal;
    }
    if (componentId) {
      this.modalService.open(componentId as LazyComponentId);
    }
  }

  private handleNavigateToAnchorActionType(nbaConfig: NbaConfig): void {
    this.store
      .select(selectCurrentMotorization)
      .pipe(
        filter((vehicle): vehicle is MotorizationVehicle => !!vehicle),
        mergeMap(vehicle => this.linkService.getConfigurationSummaryLink(vehicle.vehicleId).pipe(first())),
        first()
      )
      .subscribe((link: string): void => {
        this.router
          .navigate([link], {
            fragment: (nbaConfig.data as NbaScrollToAnchorData)?.anchor || undefined,
            queryParamsHandling: 'preserve'
          })
          .catch(noop);
      });
  }

  private handleScrollToAnchorActionType(nbaConfig: NbaConfig): void {
    this.scrollingService.scrollToAnchorWithHeaderConsideration((nbaConfig.data as NbaScrollToAnchorData)?.anchor);
  }

  private handlePrintActionType(): void {
    this.printService.print();
  }

  private handleSaveToAccountActionType(): void {
    this.appDomContextService.shadowRoot.querySelector<HTMLElement>(COMPONENT_NAME)?.click();
  }

  private handleOpenFinancialCalculatorActionType(): void {
    this.store.dispatch(financingUpdateIsFcisWidgetOpen({ isFcisWidgetOpen: true }));
  }

  private handleMonthlyRateHideActionType(): void {
    this.store.dispatch(financingUpdateIsFcisRateConfirmed({ isFcisRateConfirmed: false }));
  }

  private initPrimaryNba$(nbaConfigurations: NbaConfigurations, nbaPosition?: string): Observable<PrimaryNba> {
    /**
     * @returns the correct configuration for A/B testing (A or B) depending on config, currently assigned user group and BtO prerequesites
     */
    const toPrimaryNbaConfigConsideringAbTesting = (
      primaryNbaConfig: PrimaryNbaConfig
    ): Observable<PrimaryNbaConfig & OptionallyPrimaryNbaConfigAttachment> =>
      combineLatest([
        this.abTestingService.isFeatureEnabled(STORY_NUMBER_FE_AB_TESTING.PRIMARY_NBA),
        this.abTestingService.getUserGroupByStoryNumber(STORY_NUMBER_FE_AB_TESTING.PRIMARY_NBA),
        this.store.select(selectIsCCcIAndHasNoDealerSelected)
      ]).pipe(
        map(
          ([isFeatureEnabled, userGroup, abTestingForBtoIsEnabled]) =>
            isFeatureEnabled === true && userGroup === 'B' && !!primaryNbaConfig?.versionB && abTestingForBtoIsEnabled
        ),
        map(shouldShowVersionB => (shouldShowVersionB ? primaryNbaConfig?.versionB : primaryNbaConfig))
      );

    /**
     * Replacement e.g. for Sales Touch, meaning the default primary NBA button gets completely replaced by another button.
     */
    const toPrimaryNbaConfigOrValidReplacement = (
      primaryNbaConfig: PrimaryNbaConfig
    ): Observable<PrimaryNbaConfig & OptionallyPrimaryNbaConfigAttachment> => {
      if (primaryNbaConfig?.replacement) {
        const conditionType = primaryNbaConfig.replacement?.type;
        const conditionResolver: NbaConditionResolver = this.nbaConditionResolversCollection.get(conditionType);
        if (conditionResolver) {
          return conditionResolver.resolve$().pipe(
            map((conditionApply: boolean): PrimaryNbaConfig => {
              if (conditionApply) {
                return primaryNbaConfig.replacement.config;
              }
              return primaryNbaConfig;
            })
          );
        }
      }

      return of(primaryNbaConfig);
    };

    /**
     * Displacement e.g. for Compare teaser, meaning the default primary button gets replaced and the default primary button
     * becomes am addional secondary button (still primary NBA)
     */
    const toPrimaryNbaConfigOrValidDisplacement = (
      primaryNbaConfig: PrimaryNbaConfig
    ): Observable<PrimaryNbaConfig & OptionallyPrimaryNbaConfigAttachment> => {
      if (primaryNbaConfig?.displacement) {
        const conditionType = primaryNbaConfig.displacement?.type;
        const conditionResolver: NbaConditionResolver = this.nbaConditionResolversCollection.get(conditionType);
        if (conditionResolver) {
          return conditionResolver.resolve$().pipe(
            map((conditionApply: boolean): PrimaryNbaConfig & OptionallyPrimaryNbaConfigAttachment => {
              if (conditionApply) {
                return { ...primaryNbaConfig.displacement?.config, attachedConfig: primaryNbaConfig };
              }
              return primaryNbaConfig;
            })
          );
        }
      }

      return of(primaryNbaConfig);
    };

    return of(nbaConfigurations.primaryNba).pipe(
      switchMap((primaryNbaConfig: PrimaryNbaConfig) => toPrimaryNbaConfigConsideringAbTesting(primaryNbaConfig)),
      switchMap((primaryNbaConfig: PrimaryNbaConfig) => toPrimaryNbaConfigOrValidReplacement(primaryNbaConfig)),
      switchMap((primaryNbaConfig: PrimaryNbaConfig) => toPrimaryNbaConfigOrValidDisplacement(primaryNbaConfig)),
      map(
        (primaryNbaConfig: PrimaryNbaConfig & OptionallyPrimaryNbaConfigAttachment): PrimaryNba =>
          ({
            ...primaryNbaConfig,

            trackingPosition: nbaPosition ?? `content_${nbaPosition}`,
            href$:
              primaryNbaConfig.type === NbaActionType.Link
                ? this.processUnresolvedHref((primaryNbaConfig.data as NbaLinkData)?.link)
                : of(null),
            htmlElementType: primaryNbaConfig.type === NbaActionType.Link ? 'link' : 'button',

            // attached config for optional primary NBA secondary button (if in settings and resolved accordingly)
            attachedConfig: primaryNbaConfig.attachedConfig
              ? {
                  ...primaryNbaConfig.attachedConfig,
                  trackingPosition: nbaPosition ?? `content_${nbaPosition}`,
                  href$:
                    primaryNbaConfig.attachedConfig.type === NbaActionType.Link
                      ? this.processUnresolvedHref((primaryNbaConfig.attachedConfig.data as NbaLinkData)?.link)
                      : of(null),
                  htmlElementType: primaryNbaConfig.attachedConfig.type === NbaActionType.Link ? 'link' : 'button'
                }
              : undefined
          }) as PrimaryNba
      )
    );
  }

  private initPermanentNbas$(nbaConfigurations: NbaConfigurations): Observable<Nba[]> {
    const activeNbasStorage = new ActiveNbasStorage();
    const permanentNbas$ = activeNbasStorage.nbasAsObservable$('permanentNbas');

    from(nbaConfigurations.permanentNbas || [])
      .pipe(
        mergeMap((permanentNbaConfig: NbaConfig) =>
          this.filterByConditions$(permanentNbaConfig).pipe(
            map(
              (permanentNba: PermanentNba): PermanentNba =>
                this.createNba(permanentNba, permanentNbaConfig, 'permanent_summary')
            )
          )
        )
      )
      .subscribe(nba => {
        activeNbasStorage.updateEntry('permanentNbas', nba);
      });

    // TODO: Fix with MBCC-2656
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return permanentNbas$;
  }

  private createNba(nba: SecondaryNba, nbaConfig: NbaConfig, trackingPosition: string): SecondaryNba {
    return {
      ...nba,
      trackingPosition,
      href$:
        nbaConfig.type === NbaActionType.Link
          ? this.processUnresolvedHref((nbaConfig.data as NbaLinkData)?.link)
          : of(null),
      htmlElementType: nbaConfig.type === NbaActionType.Link ? 'link' : 'button'
    };
  }

  private initSecondaryNbas$(nbaConfigurations: NbaConfigurations, nbaPosition: string): Observable<SecondaryNba[]> {
    const activeNbasStorage = new ActiveNbasStorage();
    const secondaryNbas$ = activeNbasStorage.nbasAsObservable$('root');

    from(nbaConfigurations.secondaryNbas || [])
      .pipe(
        mergeMap((secondaryNbaConfig: SecondaryNbaConfig) =>
          this.filterByConditions$(secondaryNbaConfig).pipe(
            map((secondaryNba: SecondaryNba): SecondaryNba => {
              // treat sub nbas
              from(secondaryNba.subNbas || [])
                .pipe(
                  mergeMap(
                    (secondarySubNbaConfig: SecondaryNbaConfig): Observable<SecondaryNbaConfig> =>
                      this.filterByConditions$(secondarySubNbaConfig).pipe(
                        map(
                          (subNba: SecondaryNba): SecondaryNba =>
                            this.createNba(subNba, secondarySubNbaConfig, `content_${nbaPosition}`)
                        )
                      )
                  )
                )
                .subscribe((nba: SecondaryNba) => {
                  activeNbasStorage.updateEntry(secondaryNbaConfig.id, nba);
                });

              return {
                ...secondaryNba,
                trackingPosition: `content_${nbaPosition}`,
                href$:
                  secondaryNbaConfig.type === NbaActionType.Link
                    ? this.processUnresolvedHref((secondaryNbaConfig.data as NbaLinkData)?.link)
                    : of(null),
                htmlElementType: secondaryNbaConfig.type === NbaActionType.Link ? 'link' : 'button',
                // TODO: Fix with MBCC-2656
                // nba type hell...
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                subNbas$: activeNbasStorage.nbasAsObservable$(secondaryNbaConfig.id)
              };
            })
          )
        )
      )
      .subscribe((nba: SecondaryNba) => {
        activeNbasStorage.updateEntry('root', nba);
      });

    // TODO: Fix with MBCC-2656
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return secondaryNbas$;
  }

  private processUnresolvedHref(unresolvedHref: string): Observable<string> {
    const paramCache: Record<string, string> = {};
    const placeholders: string[] = (unresolvedHref.match(PARAM_PATTERN) || ([] as string[])).map(arrayItem =>
      arrayItem.replaceAll(BRACKET_PATTERN, '')
    );
    const resolvedParamValues$: Observable<string[]> =
      placeholders.length > 0
        ? combineLatest(
            placeholders
              .map(paramKey => this.nbaParamResolversCollection.get(paramKey))
              .filter(resolver => !!resolver)
              .map(resolver => resolver.resolve$())
          )
        : of([] as string[]);

    return resolvedParamValues$.pipe(
      map((resolvedParamValues: string[]) => {
        for (const [index, placeholder] of placeholders.entries()) {
          const cacheKey = placeholder;
          const cacheValue = resolvedParamValues[index];
          paramCache[cacheKey] = cacheValue ?? paramCache[cacheKey];
        }
        let resolvedHref = unresolvedHref;
        for (const cacheKey of Object.keys(paramCache)) {
          const cacheValueOrEmptyString = paramCache[cacheKey] || '';
          resolvedHref = resolvedHref.replace(`{${cacheKey}}`, cacheValueOrEmptyString);
        }
        return resolvedHref;
      })
    );
  }

  private filterByConditions$(nbaConfig: NbaConfig): Observable<SecondaryNba> {
    return this.mergeConditions(nbaConfig.conditions || []).pipe(
      map(
        (doConditionsApply: boolean): SecondaryNba =>
          ({
            ...nbaConfig,
            isActive: doConditionsApply
          }) as SecondaryNba
      )
    );
  }

  /**
   * Return (observable of) true if _all_ conditions apply, false otherwise
   */
  private mergeConditions(conditions: NbaConditionConfig[]): Observable<boolean> {
    const conditionObservables: Observable<boolean>[] = conditions.reduce<Observable<boolean>[]>(
      (conditionCollection, { type, params }) => {
        const conditionResolver: NbaConditionResolver = this.nbaConditionResolversCollection.get(type);
        if (conditionResolver) {
          const observable$ = conditionResolver.resolve$({ params: params || [], destroyRef: this.destroyRef });
          conditionCollection.push(observable$);
        }
        return conditionCollection;
      },
      [] as Observable<boolean>[]
    );

    if (conditionObservables.length === 0) {
      return of(true);
    }

    return combineLatest(conditionObservables).pipe(
      map(results => results.reduce((result, bool) => result && bool, true))
    );
  }
}
